import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

import Grid from "@mui/material/Grid2";
import GeneralContext from "../GeneralContext/GeneralContext";
import PageButtonWhite from "./PageButtonWhite";
import PageButtonBlack from "./PageButtonBlack";
import PageButtonBlue from "./PageButtonBlue";
import axios from "../../api/axios";

export default function ModalRecepcionInicio(props) {
  const {
    open,
    onClose,
    data = { tipo_oficio: null, rbds: [] },
    oficioFile, 
  } = props;

  const { userSesion, includeRol } = useContext(GeneralContext);
  const navigate = useNavigate();
  const rows = useMemo(() => {
    if (Array.isArray(data?.rbds)) return data.rbds;
    if (Array.isArray(data?.rbds?.rbds)) return data.rbds.rbds;
    return [];
  }, [data]);

  const isInicioServicio = data?.tipo_oficio === "INICIO_SERVICIO";
  const titulo = isInicioServicio
    ? "Ingreso de Inicio de servicio"
    : "Ingreso de Recepción de Obras";

  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);

  const [resultView, setResultView] = useState(false);
  const [resultOk, setResultOk] = useState(false);
  const [resultMsg, setResultMsg] = useState("");

  const [resultNumero, setResultNumero] = useState(null);
  const [resultFecha, setResultFecha] = useState(null);

  useEffect(() => {
    if (open) {
      setLoading(false);
      setStarted(false);
      setResultView(false);
      setResultOk(false);
      setResultMsg("");
      setResultNumero(null);
      setResultFecha(null);
    }
  }, [open, data]);

  const pickFirst = (...vals) => vals.find((v) => v != null && v !== "");

  const formatFecha = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v.slice(0, 10);
    if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
    return String(v).slice(0, 10);
  };

  const buildSuccessMessage = (resp) => {
    const d = resp?.data ?? {};

    const numero = pickFirst(d.numero_oficio, d?.oficio?.numero_oficio, data?.numero_oficio);
    const fecha = pickFirst(d.fecha_oficio, d?.oficio?.fecha_oficio, data?.fecha_oficio);

    const numTxt = numero != null ? `n°${String(numero).padStart(4, "0")}` : "n°0000";
    const fechaTxt = fecha ? ` con fecha ${formatFecha(fecha)}` : "";

    const base =
      data?.tipo_oficio === "INICIO_SERVICIO"
        ? "Se ha ingresado el Oficio de Inicio de Servicio"
        : "Se ha ingresado el Oficio de Recepción de Obras";

    return `${base} ${numTxt}${fechaTxt}.`;
  };

  const buildErrorMessage = (err) => {
    const d = err?.response?.data;
    return (
      pickFirst(d?.detail, d?.message, d?.error, d?.msg, err?.message) ||
      "Ocurrió un error al ingresar el oficio. Revisa los datos e intenta nuevamente."
    );
  };
const devengoStats = useMemo(() => {
  let devengo = 0;
  let factura = 0;

  for (const r of rows) {
    if (!!r?.devengo) devengo += 1;
    else factura += 1;
  }

  return { devengo, factura };
}, [rows]);

  const stats = useMemo(() => {
    const convocatorias = new Set();
    const operadores = new Set();
    const zonas = new Set();
    const etapas = new Set();

    for (const r of rows) {
      if (r?.convocatoria != null) convocatorias.add(String(r.convocatoria));
      if (r?.operador != null) operadores.add(String(r.operador));
      if (r?.zona != null) zonas.add(String(r.zona));
      if (r?.etapa != null) etapas.add(String(r.etapa));
    }

    return {
      convocatorias: convocatorias.size,
      operadores: operadores.size,
      zonas: zonas.size,
      etapas: etapas.size,
    };
  }, [rows]);
  const borderColor = "1px solid rgba(0, 0, 0, 0.30)";
  const cellBR = { borderRight: borderColor };

  const payloadToSend = useMemo(() => {
    if (Array.isArray(data?.rbds)) return data;

    if (data?.rbds && Array.isArray(data.rbds.rbds)) {
      const w = data.rbds;
      return { ...w, rbds: w.rbds };
    }

    return data;
  }, [data]);

  const handleSend = useCallback(async () => {
    if (!rows.length) return;

    setStarted(true);
    setLoading(true);

    try {
      const form = new FormData();
      form.append("prefactura_oficios", JSON.stringify(payloadToSend));

      if (oficioFile?.archivo) {
        form.append("file", oficioFile.archivo, oficioFile.archivo?.name || "temp.pdf");
      }

      const resp = await axios.putOficiosRBD(form);

      const d = resp?.data ?? {};
      const numero = pickFirst(d.numero_oficio, d?.oficio?.numero_oficio, payloadToSend?.numero_oficio);
      const fecha = pickFirst(d.fecha_oficio, d?.oficio?.fecha_oficio, payloadToSend?.fecha_oficio);

      setResultNumero(numero ?? null);
      setResultFecha(fecha ?? null);

      setResultOk(true);
      setResultMsg(buildSuccessMessage(resp));
      setResultView(true);
    } catch (e) {
      setResultOk(false);
      setResultMsg(buildErrorMessage(e));
      setResultView(true);
    } finally {
      setLoading(false);
    }
  }, [rows.length, payloadToSend, oficioFile, pickFirst]);

  const handleClose = () => {
    onClose?.(true);
  };

  const handleBackToForm = () => {
    setResultView(false);
    setResultOk(false);
    setResultMsg("");
    setStarted(false);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={() => (loading ? null : handleClose())}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      }}
      slotProps={{ backdrop: { sx: { opacity: "0.1!important" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "860px",
          maxHeight: "650px",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: 0,
          overflow: "hidden",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center", position: "relative" }}>
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => (loading ? null : handleClose())}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>

          {/* ===================== VISTA RESULTADO ===================== */}
          {resultView ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Box sx={{ mb: 1 }}>
                {resultOk ? (
                  <DoneOutlineOutlinedIcon color="success" sx={{ fontSize: 48 }} />
                ) : (
                  <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
                )}
              </Box>

              <Typography variant="h6" fontWeight="bold" sx={{ color: "#4D4D4D" }}>
                {resultOk ? titulo : "No se pudo completar la acción"}
              </Typography>

              <Typography variant="body2" sx={{ mt: 1, color: "#333" }}>
                {resultMsg}
                
              </Typography>

              <Box sx={{ mt: 2 }}>
                {resultOk ? (
                  <PageButtonBlue
                      width="320px"
                      onClick={() => navigate("/parque_lista")}
                    >
                      Volver a Estado de Parque
                    </PageButtonBlue>
                ) : (
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <PageButtonWhite width="220px" onClick={handleBackToForm}>
                      Volver
                    </PageButtonWhite>
                    <PageButtonBlack width="220px" onClick={handleSend} disabled={loading}>
                      Reintentar
                    </PageButtonBlack>
                  </Stack>
                )}
              </Box>
            </Box>
          ) : (
            <>
              {/* ===================== CONFIRMACIÓN INICIAL ===================== */}
              {!started && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ mb: 0 }}>
                    <ContentPasteGoIcon color="error" sx={{ fontSize: 45 }} />
                  </Box>

                  <Typography
                    variant="h5"
                    component="h2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      fontSize: "26px",
                      my: 1,
                      color: "#4D4D4D",
                      textTransform: "uppercase",
                    }}
                  >
                    {titulo}
                  </Typography>

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#333" }}>
                    Con esta acción ingresará  {isInicioServicio ? "el Inicio de Servicio" : "la Recepción de Obra"}  a {rows.length} RBDs ¿Esta seguro?
                  </Typography>

                  <Typography variant="body1" sx={{ px: 5, color: "#333" }}>
                    Esta acción no podrá deshacerse.
                  </Typography>
                </Box>
              )}

              {/* ===================== BARRA DE CARGA INDETERMINADA ===================== */}
              {loading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    No cierre esta ventana mientras se realiza el proceso
                  </Typography>
                </Box>
              )}

              {/* ===================== RESUMEN CON ÍCONOS ===================== */}
              <Box sx={{ mb: 1 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ rowGap: 1 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <FormatListNumberedIcon sx={{ color: "#4D4D4D" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {rows.length} RBDs
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <FactCheckOutlinedIcon sx={{ color: "#4D4D4D" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {stats.convocatorias} Convocatorias
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <BusinessOutlinedIcon sx={{ color: "#4D4D4D" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {stats.operadores} Operadores
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <MapOutlinedIcon sx={{ color: "#4D4D4D" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {stats.zonas} Zonas
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <LayersOutlinedIcon sx={{ color: "#4D4D4D" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {stats.etapas} Etapas
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {isInicioServicio && (
  <Stack
    direction="row"
    spacing={3}
    justifyContent="center"
    alignItems="center"
    flexWrap="wrap"
    sx={{ rowGap: 1, mt: 1 }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <ReceiptLongOutlinedIcon sx={{ color: "#4D4D4D" }} />
      <Typography variant="body2" fontWeight="bold">
        {devengoStats.factura} Factura
      </Typography>
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <DescriptionOutlinedIcon sx={{ color: "#4D4D4D" }} />
      <Typography variant="body2" fontWeight="bold">
        {devengoStats.devengo} Devengo
      </Typography>
    </Box>
  </Stack>
)}

              <Divider sx={{ my: 2 }} />

              {/* ===================== TABLA ===================== */}
              <TableContainer
                sx={{
                  maxHeight: 320,
                  overflow: "auto",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
                  mb:2,
                }}
              >
                <Table stickyHeader sx={{ borderCollapse: "collapse" }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={cellBR}>
                        RBD
                      </TableCell>
                      <TableCell align="center" sx={cellBR}>
                        Operador
                      </TableCell>
                      <TableCell align="center" sx={cellBR}>
                        Convocatoria
                      </TableCell>
                      <TableCell align="center" sx={cellBR}>
                        Zona
                      </TableCell>
                      <TableCell align="center" sx={isInicioServicio ? cellBR : undefined}>
                        Etapa
                      </TableCell>

                      {/* Columna extra solo si INICIO_SERVICIO */}
                      {isInicioServicio && (
                        <TableCell align="center">
                          Pasar a Devengo o Facturación
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow
                        key={`${row?.rbd ?? "rbd"}-${idx}`}
                        sx={{ "&:last-child td": { borderBottom: "none" } }}
                      >
                        <TableCell align="center" sx={cellBR}>
                          {row?.rbd}
                        </TableCell>
                        <TableCell align="center" sx={cellBR}>
                          {row?.operador}
                        </TableCell>
                        <TableCell align="center" sx={cellBR}>
                          {row?.convocatoria}
                        </TableCell>
                        <TableCell align="center" sx={cellBR}>
                          {row?.zona}
                        </TableCell>

                        {/* si NO hay columna extra, esta última debe quedar sin borderRight */}
                        <TableCell align="center" sx={isInicioServicio ? cellBR : undefined}>
                          {row?.etapa}
                        </TableCell>

                        {isInicioServicio && (
                          <TableCell align="center">
                            {row?.devengo ? "Devengo" : "Facturación"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ===================== BOTONERA ===================== */}
              {!started && (
                <Grid container spacing={2} sx={{ m: 1 }}>
                  <Grid size={3} />
                  <Grid size={3}>
                    <PageButtonWhite
                      onClick={handleClose}
                      width="100%"
                      disabled={loading}
                    >
                      Volver a editar
                    </PageButtonWhite>
                  </Grid>
                  <Grid size={5}>
                    <PageButtonBlack
                      onClick={handleSend}
                      disabled={loading || rows.length === 0}
                      width="100%"
                    >
                      OK, ingresar {isInicioServicio ? " Inicio de Servicio" : " Recepción de Obra"}
                    </PageButtonBlack>
                  </Grid>
                </Grid>
              )}


              {started && !loading && (
                <Box sx={{ mt: 1 }}>
                  <PageButtonBlue width="320px" onClick={handleClose}>
                    Cerrar
                  </PageButtonBlue>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
}
